import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PRESET_COLORS = [
  "#dce8e2",
  "#e8e0d2",
  "#d2dce8",
  "#e8d6d2",
  "#e2d8e8",
  "#f0ead8",
  "#ffffff",
];

/** Ignore dismiss events from the opening right-click / trackpad gesture. */
const OPEN_GRACE_MS = 400;

export function toColorInputValue(color: string): string {
  const trimmed = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return "#dce8e2";
}

export const SCENE_HEADER_PRESET_COLORS = PRESET_COLORS;

export interface SceneHeaderColorMenuProps {
  x: number;
  y: number;
  currentColor: string;
  defaultColor: string;
  hasCustomColor: boolean;
  onPick: (color: string) => void;
  onReset: () => void;
  onClose: () => void;
  /** Optional destructive action shown below color controls. */
  onDeleteScene?: () => void;
  canDeleteScene?: boolean;
  onArchiveScene?: () => void;
  /** Browse / restore archived versions of this live scene. */
  onBrowseArchiveVersions?: () => void;
  onDuplicateScene?: () => void;
  onTransferScene?: () => void;
  canTransferScene?: boolean;
  onSplitScene?: () => void;
  canSplitScene?: boolean;
  onMergeNext?: () => void;
  canMergeNext?: boolean;
  onMergePrev?: () => void;
  canMergePrev?: boolean;
  onCopySceneLink?: () => void;
}

/**
 * Context menu for scene header background.
 * Portaled to document.body and dismiss-guarded so macOS two-finger /
 * right-click does not instantly close the menu (Ctrl+click often worked
 * because it does not leave the same secondary-button event trail).
 */
export function SceneHeaderColorMenu({
  x,
  y,
  currentColor,
  defaultColor,
  hasCustomColor,
  onPick,
  onReset,
  onClose,
  onDeleteScene,
  canDeleteScene = true,
  onArchiveScene,
  onBrowseArchiveVersions,
  onDuplicateScene,
  onTransferScene,
  canTransferScene = false,
  onSplitScene,
  canSplitScene = false,
  onMergeNext,
  canMergeNext = false,
  onMergePrev,
  canMergePrev = false,
  onCopySceneLink,
}: SceneHeaderColorMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [pos, setPos] = useState({ left: x, top: y });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      left: Math.min(x, window.innerWidth - rect.width - 8),
      top: Math.min(y, window.innerHeight - rect.height - 8),
    });
  }, [x, y]);

  useEffect(() => {
    const openedAt = performance.now();

    function inGrace() {
      return performance.now() - openedAt < OPEN_GRACE_MS;
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseRef.current();
    }

    function onPointerDown(e: PointerEvent) {
      // Secondary button + leftovers from the opening gesture.
      if (e.button === 2 || e.ctrlKey || e.metaKey) return;
      if (inGrace()) return;
      if (ref.current?.contains(e.target as Node)) return;
      onCloseRef.current();
    }

    function onContextMenu(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) {
        e.preventDefault();
        return;
      }
      // Swallow the opening gesture's bubbling contextmenu / follow-ups.
      if (inGrace()) {
        e.preventDefault();
        return;
      }
      onCloseRef.current();
    }

    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("contextmenu", onContextMenu, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("contextmenu", onContextMenu, true);
    };
  }, []);

  return createPortal(
    <div
      ref={ref}
      className="sm-ctx-menu"
      style={{ left: pos.left, top: pos.top }}
      role="menu"
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="sm-ctx-menu-label">Baggrundsfarve</div>
      <div className="sm-ctx-swatches">
        {PRESET_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            className={`sm-ctx-swatch${
              toColorInputValue(currentColor).toLowerCase() === color
                ? " is-active"
                : ""
            }`}
            style={{ background: color }}
            title={color}
            aria-label={`Vælg ${color}`}
            onClick={() => {
              onPick(color);
              onClose();
            }}
          />
        ))}
        <label className="sm-ctx-swatch sm-ctx-swatch-custom" title="Tilpas">
          <input
            type="color"
            value={toColorInputValue(currentColor)}
            onChange={(e) => {
              onPick(e.target.value);
              onClose();
            }}
          />
        </label>
      </div>
      {hasCustomColor ? (
        <button
          type="button"
          className="sm-ctx-menu-item"
          onClick={() => {
            onReset();
            onClose();
          }}
        >
          Brug standard ({defaultColor})
        </button>
      ) : null}
      {onDuplicateScene ||
      onTransferScene ||
      onSplitScene ||
      onMergeNext ||
      onMergePrev ||
      onArchiveScene ||
      onBrowseArchiveVersions ||
      onDeleteScene ||
      onCopySceneLink ? (
        <div className="sm-ctx-menu-sep" />
      ) : null}
      {onCopySceneLink ? (
        <button
          type="button"
          className="sm-ctx-menu-item"
          onClick={() => {
            onCopySceneLink();
            onClose();
          }}
        >
          Kopiér link til scene
        </button>
      ) : null}
      {onDuplicateScene ? (
        <button
          type="button"
          className="sm-ctx-menu-item"
          onClick={() => {
            onDuplicateScene();
            onClose();
          }}
        >
          Dupliker scene
        </button>
      ) : null}
      {onTransferScene ? (
        <button
          type="button"
          className="sm-ctx-menu-item"
          disabled={!canTransferScene}
          title={
            canTransferScene
              ? "Kopiér eller flyt til en anden episode eller produktion"
              : "Der er ingen andre episoder eller produktioner"
          }
          onClick={() => {
            if (!canTransferScene) return;
            onTransferScene();
            onClose();
          }}
        >
          Kopiér eller flyt…
        </button>
      ) : null}
      {onSplitScene ? (
        <button
          type="button"
          className="sm-ctx-menu-item"
          disabled={!canSplitScene}
          title={
            canSplitScene
              ? "Vælg bagefter linjen mellem de to rækker, der skal skilles"
              : "Scenen skal have mindst to rækker"
          }
          onClick={() => {
            if (!canSplitScene) return;
            onSplitScene();
            onClose();
          }}
        >
          Opdel scene
        </button>
      ) : null}
      {onMergePrev ? (
        <button
          type="button"
          className="sm-ctx-menu-item"
          disabled={!canMergePrev}
          onClick={() => {
            if (!canMergePrev) return;
            onMergePrev();
            onClose();
          }}
        >
          Flet med forrige scene
        </button>
      ) : null}
      {onMergeNext ? (
        <button
          type="button"
          className="sm-ctx-menu-item"
          disabled={!canMergeNext}
          onClick={() => {
            if (!canMergeNext) return;
            onMergeNext();
            onClose();
          }}
        >
          Flet med næste scene
        </button>
      ) : null}
      {onArchiveScene ? (
        <button
          type="button"
          className="sm-ctx-menu-item"
          onClick={() => {
            onArchiveScene();
            onClose();
          }}
        >
          Arkivér scene…
        </button>
      ) : null}
      {onBrowseArchiveVersions ? (
        <button
          type="button"
          className="sm-ctx-menu-item"
          onClick={() => {
            onBrowseArchiveVersions();
            onClose();
          }}
        >
          Arkiverede versioner…
        </button>
      ) : null}
      {onDeleteScene ? (
        <button
          type="button"
          className="sm-ctx-menu-item sm-ctx-menu-item-danger"
          disabled={!canDeleteScene}
          title={
            canDeleteScene
              ? "Flyt scenen til papirkurven"
              : "Kan ikke slette den sidste scene i den sidste episode"
          }
          onClick={() => {
            if (!canDeleteScene) return;
            onDeleteScene();
            onClose();
          }}
        >
          Slet scene…
        </button>
      ) : null}
    </div>,
    document.body,
  );
}

/** Resolve effective scene header background (per-scene or default). */
export function sceneHeaderBg(
  sceneHeaderBackground: string | undefined,
  defaultBg: string,
): string {
  return sceneHeaderBackground?.trim() || defaultBg;
}
